import { useState } from 'react'
import StringList from '../components/candidateSearch/StringList'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import { MenuItems } from '../enums/MenuItems'
import Button from '../components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import roleApi from '../api/role/RoleApi'
import { SetExtra } from '../utils/SetExtra'
import personApi from '../api/person/PersonApi'
import Section from '../components/Section'
import { CandidateSearchResult } from '../api/role/models/searchCandidates/SearchCandidatesRes'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import PersonUi from '../components/PersonUi'
import RankingScore from '../components/RankingScore'
import LoadingSpinner from '../components/LoadingSpinner'
import { toast, Toaster } from 'sonner'
import Tabs from '../components/Tabs'
import FullRanking from '../components/candidateSearch/FullRanking'
import BooleanSearch from '../components/candidateSearch/BooleanSearch'
import { ManageRoleProvider } from '../components/manageRole/stateStore/ManageRoleProvider'

export default function CandidateSearchVectorTests(): React.JSX.Element {
    const [searchPhrases, setSearchPhrases] = useState<string[]>([
        '4+ years of relevant experience as a data analyst',
        'Proficiency in data analysis tools such as SQL, Python, R, or Excel',
        "Bachelor's degree in Data Science, Statistics, Mathematics, Computer Science",
    ])
    const [candidateSearchResults, setCandidateSearchResults] = useState<
        CandidateSearchResult[]
    >([])
    const [personLites, setPersonLites] = useState<PersonLiteDto[]>([])
    const [isLoading, setIsLoading] = useState(false)

    async function search() {
        setIsLoading(true)
        setCandidateSearchResults([])
        setPersonLites([])

        try {
            const candidatesIds = new SetExtra<string>()
            const res = await roleApi.searchCandidates(searchPhrases)
            res.candidateSearchResults.forEach((result) => {
                candidatesIds.add(result.candidateId)
            })

            const getPersonsLiteRes = await personApi.getPersonsLite({
                personIds: candidatesIds.getValuesAsArray(),
            })

            setPersonLites(getPersonsLiteRes.persons)
            setCandidateSearchResults(res.candidateSearchResults)
        } catch (err) {
            console.error(err)
            toast.error(
                'There was an error when searching candidates, please try again',
                {
                    duration: 5000,
                },
            )
        } finally {
            setIsLoading(false)
        }
    }

    // Create a sorted copy of the candidates by highest average match
    const sortedCandidates = candidateSearchResults.slice().sort((a, b) => {
        const computeAverage = (result: CandidateSearchResult) => {
            const allMatches = result.searchPhraseWithMatches.flatMap(
                (phrase) => phrase.matches,
            )
            return allMatches.length
                ? allMatches.reduce((sum, match) => sum + match.distance, 0) /
                      allMatches.length
                : 0
        }
        // Lower average distance is better, so sort in ascending order.
        return computeAverage(a) - computeAverage(b)
    })

    return (
        <LayoutSingleView activeMenuItem={MenuItems.CandidateSearchVectorTests}>
            <Tabs
                tabs={[
                    {
                        label: 'Boolean Search',
                        content: (
                            <ManageRoleProvider>
                                <BooleanSearch />
                            </ManageRoleProvider>
                        ),
                    },
                    {
                        label: 'Generic Embedding Only',
                        content: (
                            <div className="">
                                <div>
                                    <StringList
                                        addButtonText="Add Search Phrase"
                                        value={searchPhrases}
                                        onChange={setSearchPhrases}
                                    />
                                    <Button
                                        onClick={search}
                                        type="button"
                                        style="primary"
                                        classes="mt-4"
                                        enabled={!isLoading}
                                    >
                                        {isLoading && (
                                            <LoadingSpinner spinnerSize={24} />
                                        )}
                                        {!isLoading && (
                                            <FontAwesomeIcon
                                                icon={faMagnifyingGlass}
                                            />
                                        )}
                                        <div className="ml-2">Search</div>
                                    </Button>
                                </div>
                                {sortedCandidates.length > 0 &&
                                personLites.length > 0 ? (
                                    <Section
                                        title="Search Results"
                                        className="mt-4"
                                    >
                                        {sortedCandidates.map((result) => {
                                            const person = personLites.find(
                                                (personLite) =>
                                                    personLite.id ===
                                                    result.candidateId,
                                            )

                                            if (!person) {
                                                return null
                                            }

                                            return (
                                                <div
                                                    key={result.candidateId}
                                                    className="candidate-result mb-6"
                                                >
                                                    <PersonUi person={person} />
                                                    {result.searchPhraseWithMatches.map(
                                                        (
                                                            phraseWithMatches,
                                                            idx,
                                                        ) => (
                                                            <div
                                                                key={idx}
                                                                className="search-phrase mt-3 p-2 border rounded"
                                                            >
                                                                <h4 className="text-lg font-semibold">
                                                                    {
                                                                        phraseWithMatches.searchPhrase
                                                                    }
                                                                </h4>
                                                                {phraseWithMatches
                                                                    .matches
                                                                    .length >
                                                                0 ? (
                                                                    <ul className="list-disc ml-6 mt-2">
                                                                        {phraseWithMatches.matches.map(
                                                                            (
                                                                                match,
                                                                                matchIdx,
                                                                            ) => (
                                                                                <li
                                                                                    key={
                                                                                        matchIdx
                                                                                    }
                                                                                    className="match-item mb-1"
                                                                                >
                                                                                    <div>
                                                                                        <strong>
                                                                                            Type:
                                                                                        </strong>{' '}
                                                                                        {
                                                                                            match.data_type
                                                                                        }
                                                                                    </div>
                                                                                    <div>
                                                                                        <strong>
                                                                                            Match:
                                                                                        </strong>{' '}
                                                                                        {
                                                                                            match.text
                                                                                        }
                                                                                    </div>
                                                                                    <div>
                                                                                        <strong>
                                                                                            Distance:
                                                                                        </strong>{' '}
                                                                                        <RankingScore
                                                                                            reverse={
                                                                                                true
                                                                                            }
                                                                                            value={parseFloat(
                                                                                                (
                                                                                                    match.distance *
                                                                                                    100
                                                                                                ).toFixed(
                                                                                                    4,
                                                                                                ),
                                                                                            )}
                                                                                        />
                                                                                    </div>
                                                                                    {match.data_type ===
                                                                                        'WORKED_AT_JOB_TITLE' &&
                                                                                        match.attributes && (
                                                                                            <div>
                                                                                                {match
                                                                                                    .attributes
                                                                                                    .start_date && (
                                                                                                    <span>
                                                                                                        <strong>
                                                                                                            Start
                                                                                                            Date:
                                                                                                        </strong>{' '}
                                                                                                        {
                                                                                                            match
                                                                                                                .attributes
                                                                                                                .start_date
                                                                                                        }{' '}
                                                                                                    </span>
                                                                                                )}
                                                                                                {match
                                                                                                    .attributes
                                                                                                    .end_date && (
                                                                                                    <span>
                                                                                                        <strong>
                                                                                                            End
                                                                                                            Date:
                                                                                                        </strong>{' '}
                                                                                                        {
                                                                                                            match
                                                                                                                .attributes
                                                                                                                .end_date
                                                                                                        }
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                </li>
                                                                            ),
                                                                        )}
                                                                    </ul>
                                                                ) : (
                                                                    <p>
                                                                        No
                                                                        matches
                                                                        found.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </Section>
                                ) : (
                                    <></>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: 'Full Ranking',
                        content: <FullRanking />,
                    },
                ]}
            />

            <Toaster />
        </LayoutSingleView>
    )
}
